import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { TurnSlots } from "../thread/slots";

/** `thought`, in the terminal register — collapsed to a summary line with a
 * disclosure triangle, expanded to dim indented text.
 *
 * This is the one slot where the terminal keeps the native skin's default
 * rather than inverting it. `tool` puts its output inline because a reader
 * skims past indented output by moving their eye; reasoning is different in
 * kind, not just in length — it is the agent's working, and a reader who
 * wanted it will ask. Every vendor separates it for the same reason, and the
 * measured corpus has ZERO of these records, so the default is inherited from
 * that agreement rather than from evidence. */
@component({ name: "std-term-turn-thought" })
export class TermTurnThought extends LonaWebComponent {
  private $summary: HTMLElement = this.$("summary");
  private $label: HTMLElement = this.$("label");
  private $marker: HTMLElement = this.$("marker");
  private $text: HTMLElement = this.$("text");
  private bound = false;

  static readonly DEFAULT_SUMMARY = "Thought";

  static makeWith(props: TurnSlots.Props["thought"]): TermTurnThought {
    return (TermTurnThought.make() as TermTurnThought).bind(props);
  }

  bind(props: TurnSlots.Props["thought"]): this {
    this.$label.textContent = props.summary ?? TermTurnThought.DEFAULT_SUMMARY;
    this.$text.textContent = props.text;
    this.setExpanded(props.expanded === true);
    // Pooled components re-run bind(); the listener must attach once or a
    // recycled row toggles several times per click. Through the gesture
    // manager, so a drag ending on the summary does not toggle it.
    if (!this.bound) {
      this.bound = true;
      GESTURE_MANAGER.addPointerEvent(this.$summary, {
        onClick: () => {
          this.setExpanded(this.$text.style.display === "none");
        },
      });
    }
    return this;
  }

  private setExpanded(expanded: boolean): void {
    this.$text.style.display = expanded ? "" : "none";
    this.$marker.textContent = expanded ? "▾" : "▸";
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-ter, #8b93a7);
      }
      #summary {
        display: flex;
        align-items: baseline;
        gap: 1ch;
        cursor: pointer;
        user-select: none;
      }
      #summary:hover {
        color: var(--text-sec, #5c6370);
      }
      #marker {
        flex: none;
        width: 1ch;
      }
      #text {
        margin: 2px 0 0 0;
        padding-left: 2ch;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="summary"><span id="marker">▸</span><span id="label"></span></div>
    <pre id="text"></pre>
  `;
}
